'use client';

import React, { useEffect, useState } from 'react';

export default function MessageTeacherModal({
  isOpen,
  onClose,
  teacherId,
  teacherName,
  currentUserId,
}) {
  const [message, setMessage] = useState('');
  const [chatId, setChatId] = useState(null);

  const [creatingChat, setCreatingChat] = useState(false);
  const [sending, setSending] = useState(false);

  const [error, setError] = useState(null);

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setChatId(null);
      setError(null);
      setCreatingChat(false);
      setSending(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  async function ensureChat() {
    if (chatId || creatingChat) return chatId;

    try {
      setCreatingChat(true);
      setError(null);

      const res = await fetch('/api/chats/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          toUserId: teacherId,
          toUserName: teacherName,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || 'Failed to create chat');
      }

      const newChatId = data?.chat?.id;
      if (!newChatId) {
        throw new Error('Chat ID missing from response');
      }

      setChatId(newChatId);
      return newChatId;
    } finally {
      setCreatingChat(false);
    }
  }

  async function handleSendMessage() {
    if (!message.trim()) return;

    if (!currentUserId) {
      setError('Not logged in.');
      return;
    }

    try {
      setSending(true);
      setError(null);

      const ensuredChatId = await ensureChat();
      if (!ensuredChatId) return;

      const res = await fetch('/api/chats/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId: ensuredChatId,
          content: message.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || 'Failed to send message');
      }

      setMessage('');
      onClose();
    } catch (err) {
      setError(err.message || 'Internal server error');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-gray-500 font-bold">
              You’re sending a message to {teacherName}
            </h1>
          </div>
          <button onClick={onClose} className="text-xl">&times;</button>
        </div>

        {error && (
          <div className="mb-3 text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={500}
          rows={8}
          placeholder="Write a message to this teacher..."
          className="w-full min-h-[200px] border rounded-lg p-3 focus:outline-none focus:ring"/>

        <div className="flex justify-between items-center mt-3 text-sm text-gray-500">
          <span>{message.length}/500</span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border"
            >
              Cancel
            </button>
            <button
              onClick={handleSendMessage}
              disabled={sending || creatingChat}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send Message'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}